<template>
  <div class="q-pa-md relative-position" style="min-height: 220px;">
    <q-inner-loading :showing="loading && !reports.length">
      <loading-spinner />
    </q-inner-loading>

    <div class="row items-center q-col-gutter-sm q-mb-md">
      <div class="col-12 col-sm-auto">
        <q-select
          v-model="statusFilter"
          :options="reportStatusOptions(true)"
          outlined
          dense
          emit-value
          map-options
          options-dense
          behavior="menu"
          style="min-width: 150px;"
          @update:model-value="refresh"
        />
      </div>
      <div class="col">
        <q-input
          v-model="search"
          outlined
          dense
          clearable
          debounce="300"
          placeholder="Search reports..."
          @update:model-value="refresh"
        >
          <template #prepend>
            <q-icon name="mdi-magnify" />
          </template>
        </q-input>
      </div>
      <div
        v-if="can(permissions.canLogReports)"
        class="col-12 col-sm-auto"
      >
        <q-btn
          color="blue-7"
          icon="mdi-plus"
          label="New Report"
          no-caps
          unelevated
          class="full-width"
          @click="$emit('createReport')"
        />
      </div>
    </div>

    <no-results
      v-if="!loading && reports.length === 0"
      icon="mdi-file-chart-outline"
      title="No reports"
      :subtitle="search || statusFilter ? 'No reports match your filters.' : 'Reports created for this client will appear here.'"
      class="full-width"
    />

    <q-list
      v-else
      bordered
      separator
      class="rounded-borders"
    >
      <report-item
        v-for="report in reports"
        :key="'report-' + report.id"
        clickable
        :report="report"
        @click="$emit('viewReport', report.id)"
      >
        <div
          class="row justify-center"
          style="min-width: 40px;"
        >
          <q-btn
            v-if="report.status === 'DRAFT' && can(permissions.canEditReports)"
            dense
            flat
            round
            icon="mdi-pencil"
            color="blue-7"
            @click="continueDraft(report)"
          >
            <q-tooltip>Continue draft</q-tooltip>
          </q-btn>
        </div>
      </report-item>
    </q-list>

    <div
      v-if="hasMore"
      class="text-center q-mt-md"
    >
      <q-btn
        flat
        color="blue-7"
        no-caps
        label="Load more"
        :loading="loading"
        @click="loadMore"
      />
    </div>
  </div>
</template>

<script>
import statusMixin from 'components/app/reports/partials/statusMixin'

/*
  Lists a client's reports (drafts and published) with status/search filters and
  view / continue-draft / create actions. Reuses ReportItem (global) and the
  shared report statusMixin; viewing is delegated to the client modal's embedded
  report viewer, continuing a draft navigates to the report editor.
*/
export default {
  mixins: [statusMixin],
  props: {
    client: {
      type: Object,
      required: true
    }
  },
  emits: ['createReport', 'viewReport'],
  data () {
    return {
      loading: false,
      reports: [],
      total: 0,
      page: 1,
      rows: 20,
      statusFilter: null,
      search: ''
    }
  },
  computed: {
    hasMore () {
      return this.reports.length < this.total
    }
  },
  methods: {
    async loadReports (reset = true) {
      if (reset) {
        this.page = 1
        this.reports = []
      }
      this.loading = true
      const { status, payload } = await this.$post('v1/reportingsystem/paginate', {
        client: this.client.id,
        status: this.statusFilter,
        filter: this.search || '',
        page: this.page,
        rows: this.rows,
        sortBy: 'report_date',
        sortDirection: 'desc',
        archived: 0,
        form: null,
        staff: null,
        startDate: null,
        endDate: null
      })
      this.loading = false
      if (status === 'success') {
        this.reports = reset ? payload.reports : this.reports.concat(payload.reports)
        this.total = payload.total
      }
    },
    refresh () {
      this.loadReports(true)
    },
    loadMore () {
      this.page += 1
      this.loadReports(false)
    },
    continueDraft (report) {
      this.goTo('reports/' + report.id + '/edit')
    }
  },
  mounted () {
    this.loadReports()
    this.$bus.on('reports:reload', this.refresh)
  },
  beforeUnmount () {
    this.$bus.off('reports:reload', this.refresh)
  }
}
</script>
